namespace $.$$ {

	type $shm_hitalama_benchmarks_catalog_axis_row = readonly ( number | string )[]

	const benchmark_ids = [
		'sync-scale-benchmark',
		'sync-incremental-latency',
		'sync-incremental-latency-visible',
		'multi-user-map-sync-benchmark',
		'widget-move-sync-benchmark',
		'widget-cursor-move-benchmark',
		'widget-create-appearance-benchmark',
		'widget-create-sync-matrix-benchmark',
		'table2-entity-storage-matrix-benchmark',
		'lecturer-viewers-connect-benchmark',
		'sync-traffic-benchmark',
		'widget-file-upload-move-sync',
	] as const

	export class $shm_hitalama_benchmarks_catalog extends $.$shm_hitalama_benchmarks_catalog {

		@ $mol_mem
		results_text() {
			return this.$.$mol_fetch.text( 'shm/hitalama/benchmarks/results.md' )
		}

		spread_ids() {
			return benchmark_ids
		}

		Spread( id: string ) {
			return this.Page( id )
		}

		Spread_default() {
			return this.Page( benchmark_ids[0] )
		}

		benchmark_title( id: string ) {
			return id
		}

		@ $mol_mem_key
		benchmark_body( id: string ) {
			return [
				this.Text( id ),
				... this.benchmark_option( id ) ? [ this.Chart_box( id ) ] : [],
			]
		}

		@ $mol_mem_key
		benchmark_text( id: string ) {
			const result = this.benchmark_result( id )

			return [
				`# ${ this.benchmark_title( id ) }`,
				result,
			].filter( Boolean ).join( '\n\n' )
		}

		benchmark_result( id: string ) {
			const source = this.results_text()
			const header = `## ${ id }`
			const start = source.indexOf( header )
			if( start === -1 ) return ''

			const body_start = start + header.length
			const next = source.indexOf( '\n## ', body_start )
			return source.slice( body_start, next === -1 ? undefined : next ).trim()
		}

		benchmark_option( id: string ) {
			switch( id ) {
				case 'sync-scale-benchmark':
					return this.sync_scale_option()
				case 'sync-incremental-latency':
					return this.incremental_option()
				case 'sync-incremental-latency-visible':
					return this.incremental_visible_option()
				case 'multi-user-map-sync-benchmark':
					return this.multi_user_option()
				case 'widget-move-sync-benchmark':
					return this.widget_move_option()
				case 'widget-cursor-move-benchmark':
					return this.widget_cursor_option()
				case 'widget-create-appearance-benchmark':
					return this.appearance_option()
				case 'widget-create-sync-matrix-benchmark':
					return this.create_sync_matrix_option()
				case 'table2-entity-storage-matrix-benchmark':
					return this.entity_storage_matrix_option()
				case 'lecturer-viewers-connect-benchmark':
					return this.lecturer_viewers_option()
				case 'sync-traffic-benchmark':
					return this.sync_traffic_option()
				case 'widget-file-upload-move-sync':
					return this.file_upload_option()
				default:
					return null
			}
		}

		entity_storage_matrix_option() {
			const rows = [
				[ 'single granular', 47890, 1009, 354, 532 ],
				[ 'record land packed', 141153, 342, 113, 133 ],
				[ 'shard 16 packed', 8964, 286, 113, 134 ],
				[ 'shard 64 packed', 4528, 284, 112, 117 ],
				[ 'shard 256 packed', 4020, 280, 130, 117 ],
				[ 'shard 1000 packed', 4149, 353, 148, 200 ],
			]

			return this.option_multi_axis( 'storage, 1000 rows', rows, [
				[ 'wallMs', 'bar', 1, 0 ],
				[ 'scanMs', 'line', 2, 1 ],
				[ 'page50FirstMs', 'line', 3, 1 ],
				[ 'page50LastMs', 'line', 4, 1 ],
			], [ 'ms wall', 'ms UI/model' ] )
		}

		sync_scale_option() {
			const rows = [
				[ 10, 50, 519, 615, 16.26 ],
				[ 25, 163, 535, 808, 30.94 ],
				[ 50, 276, 550, 997, 50.15 ],
				[ 100, 386, 1079, 1714, 58.34 ],
			]

			return this.option_multi_axis( 'widgets', rows, [
				[ 'createMs', 'bar', 1, 0 ],
				[ 'syncWaitMs', 'bar', 2, 0 ],
				[ 'totalMs', 'line', 3, 0 ],
				[ 'widgetsPerSec', 'line', 4, 1 ],
			], [ 'ms', 'widgets/sec' ] )
		}

		incremental_option() {
			const rows = [
				[ 0, 0, 6, 13, 3, 1038, 1116 ],
				[ 10, 2, 2586, 3214, 2, 3875, 3916 ],
				[ 25, 3, 7212, 8906, 1, 9658, 9701 ],
				[ 50, 3, 15494, 19989, 1, 21549, 21616 ],
				[ 100, 4, 32102, 45390, 1, 49619, 49696 ],
				[ 200, 8, 60394, 96190, 1, 37531, 37631 ],
				[ 400, 8, 116287, 240919, 2, 96392, 96594 ],
			]

			return this.option_multi_axis( 'baseline widgets', rows, [
				[ 'baselineSyncWaitMs', 'bar', 2, 0 ],
				[ 'incrementSyncWaitMs', 'bar', 5, 0 ],
				[ 'incrementTotalMs', 'line', 6, 0 ],
			], [ 'ms' ] )
		}

		incremental_visible_option() {
			const rows = [
				[ 0, 0, 5, 12, 1, 2067, 2132 ],
				[ 10, 2, 2588, 3169, 2, 1542, 1584 ],
				[ 25, 1, 6189, 7684, 2, 4121, 4169 ],
			]

			return this.option_multi_axis( 'baseline widgets', rows, [
				[ 'baselineSyncWaitMs', 'bar', 2, 0 ],
				[ 'incrementSyncWaitMs', 'bar', 5, 0 ],
				[ 'incrementTotalMs', 'line', 6, 0 ],
			], [ 'ms' ] )
		}

		multi_user_option() {
			const rows = [
				[ 2, 254, 141, 116, 116 ],
				[ 3, 603, 168, 135, 124 ],
				[ 5, 2186, 351, 276, 164 ],
				[ 8, 8679, 960, 1151, 260 ],
				[ 10, 19142, 2111, 2311, 507 ],
				[ 12, 41342, 9375, 5277, 2753 ],
				[ 15, 118141, 22433, 17333, 5474 ],
			]

			return this.option_multi_axis( 'users', rows, [
				[ 'original create', 'line', 1, 0 ],
				[ 'fastpatch create', 'line', 2, 0 ],
				[ 'original move', 'line', 3, 0 ],
				[ 'fastpatch move', 'line', 4, 0 ],
			], [ 'max ms' ] )
		}

		widget_move_option() {
			const rows = [
				[ '1 one 1@0', 84, 1553, 10, 1726, 13, 73, 91 ],
				[ '1 one 20@30', 63, 515, 444, 1063, 584, 1121, 1709 ],
				[ '200 one 1@0', 26662, 92711, 22895, 142311, 145, 177, 349 ],
				[ '200 one 20@30', 44513, 105769, 21675, 172223, 3108, 162, 3295 ],
			]

			return this.option_multi_axis( 'widgets target events', rows, [
				[ 'createWriterWallMs', 'bar', 1, 0 ],
				[ 'createDataAppearMs', 'bar', 2, 0 ],
				[ 'createVisualAppearMs', 'bar', 3, 0 ],
				[ 'createTotalMs', 'line', 4, 0 ],
				[ 'writerMoveMs', 'line', 5, 0 ],
				[ 'finalSyncAfterWriterMs', 'line', 6, 0 ],
				[ 'totalUntilReaderFinalMs', 'line', 7, 0 ],
			], [ 'ms' ] )
		}

		widget_cursor_option() {
			const rows = [
				[ '1 widget', 94, 2063, 14, 2252, 5626, 13019, 18659, 59.9, 16.8, 60, 16.8 ],
				[ '200 widgets', 34678, 102674, 21882, 159280, 11337, 721, 12121, 40.1, 50.1, 56.7, 16.8 ],
			]

			return this.option_multi_axis( 'cursor drag case', rows, [
				[ 'createTotalMs', 'bar', 4, 0 ],
				[ 'writerMoveMs', 'bar', 5, 0 ],
				[ 'totalUntilReaderFinalMs', 'line', 7, 0 ],
				[ 'writerFps', 'line', 8, 1 ],
				[ 'readerFps', 'line', 10, 1 ],
				[ 'writerP95FrameMs', 'line', 9, 2 ],
				[ 'readerP95FrameMs', 'line', 11, 2 ],
			], [ 'ms', 'fps', 'frame ms' ] )
		}

		appearance_option() {
			const rows = [
				[ 50, 2149.5, 42.990 ],
				[ 100, 6273.8, 82.486 ],
				[ 150, 14344.4, 161.412 ],
				[ 200, 24544.5, 204.002 ],
				[ 250, 37812.3, 265.356 ],
				[ 300, 54900.8, 341.770 ],
			]

			return this.option_multi_axis( 'visible widgets', rows, [
				[ 'elapsedMs', 'bar', 1, 0 ],
				[ 'msPerWidget', 'line', 2, 1 ],
			], [ 'elapsed ms', 'ms/widget' ] )
		}

		create_sync_matrix_option() {
			const rows = [
				[ 'text 1x0', 2226, 2425 ],
				[ 'text 1x512', 1222, 1359 ],
				[ 'text 3x0', 1921, 2193 ],
				[ 'text 3x512', 2197, 2477 ],
				[ 'text_md 1x0', 1330, 1449 ],
				[ 'text_md 1x512', 1356, 1462 ],
				[ 'text_md 3x0', 2171, 2374 ],
				[ 'text_md 3x512', 2130, 2344 ],
				[ 'code_js 1x0', 1354, 1623 ],
				[ 'code_js 1x512', 1565, 1733 ],
				[ 'code_js 3x0', 2216, 2667 ],
				[ 'code_js 3x512', 2232, 2614 ],
			]

			return this.option_multi_axis( 'type count x payload', rows, [
				[ 'dataSyncMs', 'bar', 1, 0 ],
				[ 'visibleSyncMs', 'line', 2, 0 ],
			], [ 'ms' ] )
		}

		lecturer_viewers_option() {
			const rows = [
				[ 1, 2359, 2162, 597, 111 ],
				[ 5, 2190, 1987, 844, 120 ],
				[ 10, 3628, 3344, 1742, 144 ],
				[ 20, 5498, 5056, 2564, 286 ],
				[ 40, 13408, 13061, 6671, 189 ],
			]

			return this.option_multi_axis( 'viewers', rows, [
				[ 'allViewersMs', 'bar', 1, 0 ],
				[ 'connectMaxMs', 'line', 2, 0 ],
				[ 'mapWaitMaxMs', 'line', 3, 0 ],
				[ 'moveWaitMaxMs', 'line', 4, 0 ],
			], [ 'ms' ] )
		}

		sync_traffic_option() {
			const rows = [
				[ 'create_widget', 124, 1163, 5762, 2560, 1682, 2376, 12380 ],
				[ 'move_widget', 120, 1132, 2448, 0, 346, 3344, 6138 ],
				[ 'edit_text', 119, 1130, 2706, 0, 0, 2448, 5154 ],
			]

			return this.option_multi_axis( 'action', rows, [
				[ 'writerSentBytes', 'bar', 3, 0 ],
				[ 'writerReceivedBytes', 'bar', 4, 0 ],
				[ 'readerSentBytes', 'bar', 5, 0 ],
				[ 'readerReceivedBytes', 'bar', 6, 0 ],
				[ 'totalBytes', 'line', 7, 0 ],
				[ 'waitMs', 'line', 1, 1 ],
				[ 'totalMs', 'line', 2, 1 ],
			], [ 'bytes', 'ms' ] )
		}

		file_upload_option() {
			const rows = [
				[ 0, 109, 1152, 1944, 3387, 306, 3260, 3390 ],
			]
			const theme = this.chart_theme()
			const axis = this.chart_axis()

			return {
				color: theme.color,
				textStyle: { color: theme.text },
				tooltip: {
					trigger: 'axis',
					backgroundColor: theme.back,
					borderColor: theme.line,
					textStyle: { color: theme.text },
				},
				legend: {
					top: 0,
					textStyle: { color: theme.text },
					inactiveColor: theme.muted,
				},
				grid: { left: 8, right: 24, top: 48, bottom: 48, containLabel: true },
				xAxis: { ... axis, type: 'category', data: [ 'run' ] },
				yAxis: { ... axis, type: 'value', name: 'ms' },
				series: [
					{ name: 'addFileMs', type: 'bar', data: rows.map( row => row[1] ) },
					{ name: 'writerMoveMs', type: 'bar', data: rows.map( row => row[2] ) },
					{ name: 'finalSyncAfterMoveMs', type: 'bar', data: rows.map( row => row[3] ) },
					{ name: 'totalFromUploadStartToReaderFinalMs', type: 'line', data: rows.map( row => row[4] ) },
					{ name: 'readerFirstUpdateMs', type: 'line', data: rows.map( row => row[5] ) },
					{ name: 'readerFinalUpdateMs', type: 'line', data: rows.map( row => row[6] ) },
					{ name: 'uploadSettleFromUploadStartMs', type: 'line', data: rows.map( row => row[7] ) },
				],
			}
		}

		option_multi_axis(
			x_name: string,
			rows: readonly $shm_hitalama_benchmarks_catalog_axis_row[],
			series: readonly ( readonly [ string, string, number, number ] )[],
			y_names: readonly string[],
		) {
			const theme = this.chart_theme()
			const axis = this.chart_axis()

			return {
				color: theme.color,
				textStyle: { color: theme.text },
				tooltip: {
					trigger: 'axis',
					backgroundColor: theme.back,
					borderColor: theme.line,
					textStyle: { color: theme.text },
				},
				legend: {
					top: 0,
					textStyle: { color: theme.text },
					inactiveColor: theme.muted,
				},
				grid: { left: 8, right: 24, top: 48, bottom: 48, containLabel: true },
				xAxis: { ... axis, type: 'category', name: x_name, data: rows.map( row => row[0] ) },
				yAxis: y_names.map( name => ({ ... axis, type: 'value', name }) ),
				series: series.map( ([ name, type, index, yAxisIndex ]) => ({
					name,
					type,
					yAxisIndex,
					data: rows.map( row => row[index] ),
				}) ),
			}
		}

		chart_theme() {
			return this.$.$mol_lights() ? {
				color: [ '#2563eb', '#16a34a', '#dc2626', '#9333ea', '#ea580c', '#0891b2', '#ca8a04' ],
				text: '#1f2937',
				shade: '#4b5563',
				muted: '#9ca3af',
				line: 'rgba( 75, 85, 99, .28 )',
				back: 'rgba( 255, 255, 255, .94 )',
			} : {
				color: [ '#60a5fa', '#4ade80', '#f87171', '#c084fc', '#fb923c', '#22d3ee', '#facc15' ],
				text: '#e5e7eb',
				shade: '#cbd5e1',
				muted: '#475569',
				line: 'rgba( 203, 213, 225, .26 )',
				back: 'rgba( 15, 23, 42, .94 )',
			}
		}

		chart_axis() {
			const theme = this.chart_theme()
			return {
				axisLine: { lineStyle: { color: theme.line } },
				axisTick: { lineStyle: { color: theme.line } },
				axisLabel: { color: theme.shade },
				nameTextStyle: { color: theme.shade },
				splitLine: { lineStyle: { color: theme.line } },
			}
		}

	}

}
